/**
 * The Adventure Journal — mistakes as part of the story (`docs/rpg.md` §18).
 *
 * §18 asks for a book that records "mistakes, misunderstood concepts, weak
 * skills, corrected problems, common error patterns", and shows the shape it
 * should read in: *you have encountered this challenge 4 times · skill · common
 * mistake · recommended quest*. Everything in that example already exists in
 * the platform — the attempt log holds the encounters, `misconceptions.ts`
 * names the mistake in plain words and points at the concept that repairs it,
 * and the quest board holds the review quests. This file joins them up. It
 * measures nothing new.
 *
 * Three rules hold it to that.
 *
 * **There is one definition of struggling, and it lives in the recommender.**
 * `isStruggling` is imported from `@yuan-math/learning` rather than restated
 * here. A journal with its own idea of who is stuck would write up a challenge
 * the home page does not believe in — two screens describing different
 * students from the same profile on the same morning.
 *
 * **Malformed input is invisible to all of it** (`CLAUDE.md`). An empty box or
 * a "half" typed into the answer field is not a wrong answer, so it is not an
 * encounter, it is not a misconception, and it never puts a page of the
 * student's journal in. Every read here filters on `status`, not on `correct`.
 *
 * **The journal is recent history, not an archive.** `profile.attempts` is
 * bounded at `ATTEMPT_LOG_LIMIT` (400) and the oldest records fall out of it,
 * so an encounter count is a count *within that window* and a challenge that
 * has not come up in a long time quietly stops having a page. That is the
 * honest behaviour for a diary of what is going wrong lately, and the page is
 * worded to match — "lately", never "since you began".
 *
 * And, like every private view on this platform (`CLAUDE.md` §14, §29): this is
 * the learner's own book. Nothing a teacher, a parent or a class surface reads
 * may be built on it — a log of every answer is exactly what those views are
 * forbidden to show, and a journal is that log with the wrong reader.
 */

import { misconception, type Misconception } from '@yuan-math/exercises';
import {
  computeMastery,
  isMastered,
  isStruggling,
  type AttemptRecord,
  type LearnerProfile,
  type MasteryLevel,
} from '@yuan-math/learning';
import type { QuestDef, QuestObjective } from './quests.ts';
import { conceptKingdoms } from './state.ts';
import type { Kingdom } from './types.ts';

/**
 * What the journal needs to know about the world.
 *
 * A parameter rather than an import, for the reason everything in this package
 * is one: the definitions live in `content/`, which sits above it. Note that
 * this is the *world* catalogue and not the curriculum graph — the journal
 * reasons over the student's own attempts and never walks prerequisites; that
 * is the recommender's job, and duplicating it here is the thing this file is
 * written not to do.
 */
export type JournalWorld = {
  kingdoms: readonly Kingdom[];
  quests: readonly QuestDef[];
};

/**
 * A challenge: something going wrong lately, and what to do about it.
 *
 * `misconceptionId` and its two companions are optional together. A wrong
 * answer that matched no trap and no heuristic names no misconception, and the
 * page then says the honest thing — this keeps coming up — rather than
 * inventing a diagnosis.
 */
export type JournalChallenge = {
  kind: 'challenge';
  /** Stable across renders and across days, so `journalSeen` can hold it. */
  id: string;
  conceptId: string;
  /** Wrong answers on this concept inside the attempt-log window. */
  encounters: number;
  /** The most recent of them, in epoch milliseconds. The sort key. */
  at: number;
  /** The commonest named misconception among those wrong answers. */
  misconceptionId?: string;
  /** The catalog's plain-words account of it, written without blame. */
  description?: string;
  /** The concept that repairs it, per the catalog. */
  remediationConceptId?: string;
  /** The review quest the recommendation points at, when one matched. */
  questId?: string;
  /** Where "recommended quest" goes: the quest, or the practice that repairs it. */
  href: string;
};

/**
 * A conquered challenge: it used to go wrong, and now it does not.
 *
 * The comeback story, and the reason the journal is worth opening at all. A
 * book of nothing but current failures is a list of ways to feel behind.
 */
export type JournalConquered = {
  kind: 'conquered';
  id: string;
  conceptId: string;
  /** Wrong answers it cost, inside the window. The scar, so to speak. */
  struggledCount: number;
  /**
   * When they last practised it — the closest thing to a "mastered at" the
   * profile honestly holds, because nothing stores the moment mastery was
   * reached and inventing one would be a fact about the student we made up.
   */
  at: number;
  /** Mastery as it stands *now*, recomputed with decay like everywhere else. */
  masteryLevel: MasteryLevel;
  /** The overall figure behind that level, 0–1. */
  mastery: number;
};

export type JournalEntry = JournalChallenge | JournalConquered;

/**
 * The journal, newest trouble first.
 *
 * Challenges before conquests, deliberately: the top of the page is the thing
 * to go and do, and the victories are what the student scrolls past on the way
 * to it. Within each group, most recent evidence first.
 *
 * Pure, and takes `now` — mastery decays, so whether a concept counts as
 * conquered is a question about a moment.
 */
export function journalEntries(profile: LearnerProfile, world: JournalWorld, now: number): JournalEntry[] {
  const wrongByConcept = wrongAttempts(profile.attempts);
  const kingdomOf = conceptKingdoms(world.kingdoms);

  const challenges: JournalChallenge[] = [];
  const conquered: JournalConquered[] = [];

  for (const [conceptId, wrongs] of wrongByConcept) {
    const state = profile.concepts[conceptId];
    if (!state) continue;
    const mastery = computeMastery(state, now);

    /*
     * Mastered wins over struggling, and both can be true at once: a concept
     * answered wrongly six times and correctly thirty is still `attempts >
     * correct`, which is what `isStruggling` reads. Filing that as a current
     * problem would be the journal calling a student's best recovery their
     * worst subject.
     */
    if (isMastered(state, now)) {
      conquered.push({
        kind: 'conquered',
        id: entryId('conquered', conceptId),
        conceptId,
        struggledCount: wrongs.length,
        at: state.lastPracticedAt ?? wrongs[wrongs.length - 1]?.at ?? 0,
        masteryLevel: mastery.level,
        mastery: mastery.overall,
      });
      continue;
    }

    // The recommender's definition, not a second one. A concept whose only
    // wrong answers have rolled out of the window has nothing to write up, so
    // the log — not the state — is what starts this loop.
    if (!isStruggling(state)) continue;

    const named = commonestMisconception(wrongs);
    const remediationConceptId = named?.remediationConceptId;
    const quest = matchingReviewQuest(world, kingdomOf.get(conceptId), conceptId, remediationConceptId);

    challenges.push({
      kind: 'challenge',
      id: entryId('challenge', conceptId),
      conceptId,
      encounters: wrongs.length,
      at: wrongs[wrongs.length - 1]?.at ?? 0,
      ...(named ? { misconceptionId: named.id, description: named.description } : {}),
      ...(remediationConceptId ? { remediationConceptId } : {}),
      ...(quest ? { questId: quest.id } : {}),
      /*
       * The quest when the world has one for this, and practice otherwise —
       * on the concept the catalog names as the repair, which is the whole
       * point of a misconception carrying one. With no misconception there is
       * nothing underneath to send them to, so the concept itself is the work.
       */
      href: quest ? `/adventure/quests/${quest.id}` : `/practice/${remediationConceptId ?? conceptId}`,
    });
  }

  const byRecency = (a: { at: number }, b: { at: number }): number => b.at - a.at;
  return [...challenges.sort(byRecency), ...conquered.sort(byRecency)];
}

/** The id a `journalSeen` entry is stored under. Kind included: a challenge that becomes a conquest is news again. */
export const entryId = (kind: JournalEntry['kind'], conceptId: string): string => `${kind}:${conceptId}`;

/**
 * Whether this entry is new to the student.
 *
 * Exported so the page and its tests cannot disagree about what the dot means.
 * `seen` is `profile.rpg.journalSeen`, which is opaque to `@yuan-math/learning`
 * and given its shape here.
 */
export function isUnseen(entry: JournalEntry, seen: readonly string[]): boolean {
  return !seen.includes(entry.id);
}

/* -------------------------------------------------------------------------- */

/**
 * Wrong answers, by concept, oldest first.
 *
 * `status === 'incorrect'` rather than `!correct`: malformed input is not a
 * wrong answer and has no place in a record of what the student got wrong.
 */
function wrongAttempts(attempts: readonly AttemptRecord[]): Map<string, AttemptRecord[]> {
  const out = new Map<string, AttemptRecord[]>();
  for (const attempt of attempts) {
    if (attempt.status !== 'incorrect') continue;
    const list = out.get(attempt.conceptId);
    if (list) list.push(attempt);
    else out.set(attempt.conceptId, [attempt]);
  }
  return out;
}

/**
 * The mistake being made most often — "the common error pattern" of §18.
 *
 * Counted over this concept's wrong answers only, and over the *log* rather
 * than `ConceptState.misconceptions`: that counter is a lifetime tally with no
 * timestamps in it, so it cannot say which mistake is the current one, and a
 * habit broken in March would still be the headline in September.
 *
 * Ties go to the one seen most recently, then to the id, so the page never
 * reshuffles itself between two equally common mistakes.
 */
function commonestMisconception(wrongs: readonly AttemptRecord[]): Misconception | undefined {
  const counts = new Map<string, { count: number; last: number }>();
  for (const attempt of wrongs) {
    const id = attempt.misconceptionId;
    if (!id) continue;
    const seen = counts.get(id);
    if (seen) counts.set(id, { count: seen.count + 1, last: Math.max(seen.last, attempt.at) });
    else counts.set(id, { count: 1, last: attempt.at });
  }

  const best = [...counts.entries()].sort((a, b) => {
    if (a[1].count !== b[1].count) return b[1].count - a[1].count;
    if (a[1].last !== b[1].last) return b[1].last - a[1].last;
    return a[0].localeCompare(b[0]);
  })[0];

  // An id the catalog does not carry is not a diagnosis. Dropping it is the
  // same refusal `content.test.ts` enforces on authored traps.
  return best ? misconception(best[0]) : undefined;
}

/**
 * A review quest in this concept's kingdom that covers the work.
 *
 * The remediation concept first, because that is where the student is being
 * sent; the struggling concept second, because §18's example names a quest
 * about the skill itself. Kingdom-local, so a fraction problem never
 * recommends an errand three kingdoms away.
 *
 * Review quests are never locked (`questProgress` locks main quests and
 * nothing else), so one offered here is always one the student can take on.
 * `complete-lessons` objectives cannot be matched — resolving a lesson to its
 * concept needs the curriculum registry, and the journal deliberately does not
 * take one — which costs nothing today: every review quest in `content/` is
 * stated as practice, and the fallback is a practice link either way.
 */
function matchingReviewQuest(
  world: JournalWorld,
  kingdomId: string | undefined,
  conceptId: string,
  remediationConceptId: string | undefined,
): QuestDef | undefined {
  if (kingdomId === undefined) return undefined;
  const candidates = world.quests.filter((quest) => quest.kind === 'review' && quest.kingdomId === kingdomId);
  const wanted = [remediationConceptId, conceptId].filter((id): id is string => id !== undefined);
  for (const target of wanted) {
    const found = candidates.find((quest) => namesConcept(quest.objective, target));
    if (found) return found;
  }
  return undefined;
}

/** Does this objective ask for work on this concept? See the note above about lessons. */
function namesConcept(objective: QuestObjective, conceptId: string): boolean {
  switch (objective.kind) {
    case 'master-concepts':
    case 'practice':
      return objective.conceptIds.includes(conceptId);
    case 'complete-lessons':
    case 'clear-quests':
      return false;
  }
}

